// frontend/src/components/Booking/BookingStats.tsx
// Mobile-first compact booking stats component using unified StatsGrid

import React from 'react';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  // TrendingDown, // Unused but available for future features
  DollarSign,
  Users,
  Building2,
  BarChart3
} from 'lucide-react';
import { StatsGrid, StatItem } from '@/components/common/ui';
import { BookingStats as StatsType } from '@/types/booking';

interface BookingStatsProps {
  stats: StatsType;
  currentFilter?: string;
  onFilterChange?: (status: string) => void;
  useNavigation?: boolean;
  showAdminStats?: boolean;
  isLoading?: boolean;
}

const BookingStats: React.FC<BookingStatsProps> = ({
  stats,
  currentFilter = 'all',
  onFilterChange,
  showAdminStats = false,
  isLoading
}) => {
  
  if (isLoading || !stats) {
    return (
      <div className="space-y-4">
        <StatsGrid
          stats={[]}
          isLoading={true}
          loadingItems={4}
          layout="compact"
          columns={{ mobile: 2, tablet: 2, desktop: 4 }}
        />
        {showAdminStats && (
          <StatsGrid
            stats={[]}
            isLoading={true}
            loadingItems={3}
            layout="detailed"
            columns={{ mobile: 1, tablet: 3, desktop: 3 }}
          />
        )}
      </div>
    );
  }

  const basicStatItems: StatItem[] = [
    {
      label: 'Total',
      value: stats.total || 0,
      icon: BarChart3,
      color: 'blue',
      description: stats.total_change ? `${stats.total_change > 0 ? '+' : ''}${stats.total_change}% change` : undefined
    },
    {
      label: 'Pending',
      value: stats.pending || 0,
      icon: Clock,
      color: 'yellow',
      description: stats.pending_change ? `${stats.pending_change > 0 ? '+' : ''}${stats.pending_change}% change` : undefined
    },
    {
      label: 'Active',
      value: stats.accepted || 0,
      icon: AlertCircle,
      color: 'blue',
      description: stats.accepted_change ? `${stats.accepted_change > 0 ? '+' : ''}${stats.accepted_change}% change` : undefined
    },
    {
      label: 'Completed',
      value: stats.completed || 0,
      icon: CheckCircle,
      color: 'green',
      description: stats.completed_change ? `${stats.completed_change > 0 ? '+' : ''}${stats.completed_change}% change` : undefined
    }
  ];

  // Admin-specific advanced stats
  const adminStatItems: StatItem[] = showAdminStats ? [
    {
      label: 'Revenue',
      value: `$${(stats.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'green',
      description: stats.revenue_change ? `${stats.revenue_change > 0 ? '+' : ''}${stats.revenue_change}% change` : undefined
    },
    {
      label: 'Providers',
      value: stats.active_providers || 0,
      icon: Building2,
      color: 'purple',
      description: stats.providers_change ? `${stats.providers_change > 0 ? '+' : ''}${stats.providers_change}% change` : undefined
    },
    {
      label: 'Customers',
      value: stats.active_customers || 0,
      icon: Users,
      color: 'indigo',
      description: stats.customers_change ? `${stats.customers_change > 0 ? '+' : ''}${stats.customers_change}% change` : undefined
    }
  ] : [];

  // Custom click handlers for filtering (preserved functionality)
  const handleStatClick = (index: number) => {
    if (!onFilterChange) return;
    
    const filterKeys = ['all', 'pending', 'accepted', 'completed'];
    onFilterChange(filterKeys[index]);
  };

  return (
    <div className="space-y-4">
      {/* Main Stats - Clickable for filtering */}
      <div onClick={(e) => {
        const target = e.target as HTMLElement;
        const statCard = target.closest('[data-stat-index]');
        if (statCard) {
          const index = parseInt(statCard.getAttribute('data-stat-index') || '0');
          handleStatClick(index);
        }
      }}>
        <StatsGrid
          stats={basicStatItems.map((stat, index) => ({
            ...stat,
            // Add data attribute for click handling
            customProps: { 'data-stat-index': index }
          }))}
          layout="compact"
          columns={{ mobile: 2, tablet: 2, desktop: 4 }}
          className="cursor-pointer"
        />
      </div>

      {/* Admin Stats - Separate Section */}
      {showAdminStats && adminStatItems.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            System Overview
          </h3>
          <StatsGrid
            stats={adminStatItems}
            layout="detailed"
            columns={{ mobile: 1, tablet: 3, desktop: 3 }}
          />
        </div>
      )}
    </div>
  );
};

export default BookingStats;